import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'

import { useDebounce } from '@img-arena/consoles-utils'
import { Divider, Stack, TextField } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { AutoCompleteOption } from '@/components/inputs/LicenseCompetitionsAutocomplete/LicenseCompetitionsAutocomplete'
import { useGetSportSeasonsWithPagination } from '@/service/sportsService'
import { formikSaveButtonProps } from '@/utils/forms'

import { CompetitionsList } from './CompetitionsList'

type CompetitionsDialogProps = {
  open: boolean
  onClose: () => void
  sportType: string
  selectedCompetitions: string[]
  onSave: (competitions: string[]) => void
}

type CompetitionsFormValues = {
  competitions: string[]
}

export const CompetitionsDialog: FC<CompetitionsDialogProps> = ({
  open,
  onClose,
  sportType,
  selectedCompetitions,
  onSave
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isAutoLoading, setIsAutoLoading] = useState(false)
  const [hasMissingIds, setHasMissingIds] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, isInitialLoad ? 0 : 300)
  const hasRequestedRef = useRef(false)
  const autoLoadAttemptsRef = useRef(0)

  const shouldFetch = useMemo(() => open && !!sportType, [open, sportType])
  const effectiveSportType = useMemo(
    () => (shouldFetch ? sportType : null),
    [shouldFetch, sportType]
  )
  const effectiveSearchQuery = useMemo(
    () => (shouldFetch ? debouncedSearchQuery : undefined),
    [shouldFetch, debouncedSearchQuery]
  )

  const {
    data: rawCompetitions,
    isLoading,
    isLoadingMore,
    loadMore,
    hasMore,
    currentPage
  } = useGetSportSeasonsWithPagination(effectiveSportType, effectiveSearchQuery)

  const competitions = useMemo(() => {
    if (!rawCompetitions || !Array.isArray(rawCompetitions)) return []

    const seen = new Set()
    return rawCompetitions.filter((comp) => {
      if (seen.has(comp.value)) {
        return false
      }
      seen.add(comp.value)
      return true
    })
  }, [rawCompetitions])

  const initialCompetitionsRef = useRef<string[]>([])

  const formik = useFormik<CompetitionsFormValues>({
    initialValues: {
      competitions: selectedCompetitions || []
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values.competitions)
      onClose()
    },
    validate: (values) => {
      const errors: Partial<Record<keyof CompetitionsFormValues, string>> = {}
      const initial = initialCompetitionsRef.current || []
      const current = values.competitions || []

      const initialSorted = [...initial].sort()
      const currentSorted = [...current].sort()

      const hasChanged =
        initialSorted.length !== currentSorted.length ||
        !initialSorted.every((id, index) => id === currentSorted[index])

      if (!hasChanged) {
        errors.competitions = 'No changes made'
      }

      return errors
    }
  })

  const handleCompetitionToggle = useCallback(
    (competition: AutoCompleteOption) => {
      const currentSelectedIds = formik.values.competitions || []
      const isSelected = currentSelectedIds.includes(competition.value)

      if (isSelected) {
        formik.setFieldValue(
          'competitions',
          currentSelectedIds.filter((id) => id !== competition.value)
        )
      } else {
        formik.setFieldValue('competitions', [...currentSelectedIds, competition.value])
      }
    },
    [formik]
  )

  const { selectedCompetitionsList, unselectedCompetitionsList } = useMemo(() => {
    const selectedIds = (formik.values.competitions || []).map((id) => String(id))
    const isSearching = debouncedSearchQuery && debouncedSearchQuery.trim() !== ''

    if (!competitions || !Array.isArray(competitions)) {
      return { selectedCompetitionsList: [], unselectedCompetitionsList: [] }
    }

    if (isSearching) {
      const selectedInSearchResults = competitions.filter(
        (comp) => comp && comp.value && selectedIds.includes(String(comp.value))
      )

      return {
        selectedCompetitionsList: selectedInSearchResults,
        unselectedCompetitionsList: competitions
      }
    } else {
      const selected = competitions.filter(
        (comp) => comp && comp.value && selectedIds.includes(String(comp.value))
      )
      const unselected = competitions.filter(
        (comp) => comp && comp.value && !selectedIds.includes(String(comp.value))
      )

      return { selectedCompetitionsList: selected, unselectedCompetitionsList: unselected }
    }
  }, [competitions, formik.values.competitions, debouncedSearchQuery])

  useEffect(() => {
    if (open && !hasRequestedRef.current) {
      setSearchQuery('')
      setIsInitialLoad(true)
      hasRequestedRef.current = true
      autoLoadAttemptsRef.current = 0
      setIsAutoLoading(false)
      setHasMissingIds(false)
      initialCompetitionsRef.current = [...(selectedCompetitions || [])]
    } else if (!open) {
      hasRequestedRef.current = false
      autoLoadAttemptsRef.current = 0
      setIsAutoLoading(false)
      setHasMissingIds(false)
      initialCompetitionsRef.current = []
    }
  }, [open, selectedCompetitions])

  useEffect(() => {
    if (competitions?.length && isInitialLoad) {
      setIsInitialLoad(false)
      setHasMissingIds(false)
    }
  }, [competitions, isInitialLoad])

  useEffect(() => {
    if (!open || !selectedCompetitions || !selectedCompetitions.length) return
    if (!competitions || !competitions.length) return
    if (isLoading || isLoadingMore) return
    if (autoLoadAttemptsRef.current >= 50) {
      return
    }

    const selectedIds = selectedCompetitions
    const foundSelectedIds = competitions
      .filter((comp) => selectedIds.includes(comp.value))
      .map((comp) => comp.value)

    const missingIds = selectedIds.filter((id) => !foundSelectedIds.includes(id))

    if (missingIds.length && hasMore && !isLoadingMore) {
      setIsAutoLoading(true)
      setHasMissingIds(true)
      autoLoadAttemptsRef.current += 1
      loadMore(currentPage + 1)
    } else if (!missingIds.length) {
      setIsAutoLoading(false)
      setHasMissingIds(false)
    } else if (!hasMore) {
      setIsAutoLoading(false)
      setHasMissingIds(false)
    }
  }, [
    open,
    selectedCompetitions,
    competitions,
    hasMore,
    currentPage,
    isLoading,
    isLoadingMore,
    loadMore,
    debouncedSearchQuery
  ])

  useEffect(() => {
    if (!isLoadingMore && isAutoLoading) {
      const timer = setTimeout(() => {
        setIsAutoLoading(false)
        setHasMissingIds(false)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [isLoadingMore, isAutoLoading])

  useEffect(() => {
    if (open) {
      autoLoadAttemptsRef.current = 0
      setIsAutoLoading(false)
    }
  }, [debouncedSearchQuery, open])

  const handleClose = () => {
    formik.resetForm()
    setSearchQuery('')
    setIsAutoLoading(false)
    onClose()
  }

  return (
    <ConsoleDialog
      open={open}
      onClose={handleClose}
      title="Selected Competitions"
      dialogSize="lg"
      SaveButtonProps={{
        ...formikSaveButtonProps(formik),
        onClick: () => formik.handleSubmit(),
        children: 'Save'
      }}
    >
      <Stack spacing={2} sx={{ height: '500px' }}>
        <TextField
          placeholder="Search competitions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
        />

        <Divider />

        <CompetitionsList
          isLoading={isLoading}
          debouncedSearchQuery={debouncedSearchQuery}
          hasMissingIds={hasMissingIds}
          isAutoLoading={isAutoLoading}
          competitions={competitions}
          selectedCompetitionsList={selectedCompetitionsList}
          unselectedCompetitionsList={unselectedCompetitionsList}
          selectedCompetitionIds={formik.values.competitions}
          onCompetitionToggle={handleCompetitionToggle}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          currentPage={currentPage}
          loadMore={loadMore}
        />
      </Stack>
    </ConsoleDialog>
  )
}
